import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "../Api";
import { parseDecimalInput, validateMaterials, validateExtraWork } from "../utils/validation";
import { generatePDF } from "../utils/pdfGenerator";
import { useNavigate } from "react-router-dom";
import {
    validateFile,
    buildFormData,
    uploadWithProgress,
    createPreviewUrl,
    revokePreviewUrls,
    FileValidationError
} from "../utils/fileUpload";

export function useTimesheetForm(showFlash, initialTimesheet, preselectedProjectId = null) {
    const { t } = useTranslation();
    // State management
    const [currentDate, setCurrentDate] = useState(
        initialTimesheet?.week_start_date ? new Date(initialTimesheet.week_start_date) : new Date()
    );
    const [hours, setHours] = useState(
        initialTimesheet?.hours_worked
            ? Array.isArray(initialTimesheet.hours_worked)
                ? initialTimesheet.hours_worked
                : objectToDayArray(initialTimesheet.hours_worked)
            : Array(7).fill(0)
    );
    const [meters, setMeters] = useState(
        initialTimesheet?.meters_worked
            ? Array.isArray(initialTimesheet.meters_worked)
                ? initialTimesheet.meters_worked
                : objectToDayArray(initialTimesheet.meters_worked)
            : Array(7).fill(0)
    );
    const [extraWork, setExtraWork] = useState(initialTimesheet?.extra_work || []);
    const [materials, setMaterials] = useState(initialTimesheet?.materials || []);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [loggedInUser, setLoggedInUser] = useState(null); // For role checking
    const [targetUser, setTargetUser] = useState(null); // User to create timesheet for
    const [projectUsers, setProjectUsers] = useState([]); // Users in selected project
    const [selectedTargetUserId, setSelectedTargetUserId] = useState(null); // Selected user ID for timesheet
    const [totalHours, setTotalHours] = useState(0);
    const [totalCosts, setTotalCosts] = useState(0);
    const [rateType, setRateType] = useState("hourly");
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // File preview URLs that need cleanup
    const previewUrlsRef = useRef([]);

    const units = ["meter", "stuks", "m2"];
    const daysOfWeek = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
    const navigate = useNavigate();

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            revokePreviewUrls(previewUrlsRef.current);
        };
    }, []);

    // Utility: Convert {monday: 2, ...} to [2, 0, ...] in correct order
    function objectToDayArray(obj) {
        const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
        if (!obj) return Array(7).fill(0);
        return days.map(day => parseFloat(obj[day]) || 0);
    }

    // Fetch assigned projects and user
    useEffect(() => {
        const fetchAssignedProjectsAndUser = async () => {
            try {
                const userProfileRes = await api.get("/users/profile");
                const userData = userProfileRes.data;
                setLoggedInUser(userData); // Store logged-in user for role checking

                const userId = userData.id;

                // Owner and finance can access ALL projects
                let assignedProjectsRes;
                if (userData.role === "eigenaar" || userData.role === "financieel medewerker") {
                    assignedProjectsRes = await api.get("/projects");
                } else {
                    // ZZP/Voorman only see assigned projects
                    assignedProjectsRes = await api.get(`/users/${userId}/projects`);
                }

                setProjects(assignedProjectsRes.data);

                // Auto-select project based on preselectedProjectId or default to first project
                if (assignedProjectsRes.data.length > 0) {
                    if (preselectedProjectId) {
                        // Try to find the preselected project
                        const preselected = assignedProjectsRes.data.find(
                            p => p.id === parseInt(preselectedProjectId, 10)
                        );
                        if (preselected) {
                            setSelectedProject(preselected);
                        } else {
                            // If preselected project not found in user's projects, default to first
                            setSelectedProject(assignedProjectsRes.data[0]);
                        }
                    } else {
                        // No preselection, use first project
                        setSelectedProject(assignedProjectsRes.data[0]);
                    }
                } else {
                    setLoadingUser(false);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
                setLoadingUser(false);
            }
        };
        fetchAssignedProjectsAndUser();
    }, [preselectedProjectId]);

    // Fetch user assignment for selected project
    useEffect(() => {
        const fetchUserAssignment = async () => {
            try {
                if (!selectedProject || !loggedInUser) return;

                // Owner and finance users don't need to be assigned to projects
                if (loggedInUser.role === "eigenaar" || loggedInUser.role === "financieel medewerker") {
                    // Set a dummy user object for owner/finance
                    setUser({
                        user_id: loggedInUser.id,
                        user_name: loggedInUser.name,
                        rate_type: "hourly",
                        hourly_rate: 0,
                        meter_rate: 0
                    });
                    setRateType("hourly");
                    setLoadingUser(false);
                    return;
                }

                // For ZZP/Voorman, fetch their project assignment
                const userId = loggedInUser.id;
                const assignmentRes = await api.get(`/projects/${selectedProject.id}/users/${userId}`);
                setUser(assignmentRes.data);
                setRateType(assignmentRes.data.rate_type);

                // If no target user is set yet, default to logged-in user
                if (!targetUser && !selectedTargetUserId) {
                    setTargetUser(assignmentRes.data);
                    setSelectedTargetUserId(assignmentRes.data.user_id);
                }
            } catch (error) {
                console.error("Error fetching user assignment:", error);
                // For owner/finance, this is acceptable - they may not be assigned
                if (loggedInUser?.role === "eigenaar" || loggedInUser?.role === "financieel medewerker") {
                    setUser({
                        user_id: loggedInUser.id,
                        user_name: loggedInUser.name,
                        rate_type: "hourly",
                        hourly_rate: 0,
                        meter_rate: 0
                    });
                    setRateType("hourly");
                } else {
                    setUser(null);
                }
            } finally {
                setLoadingUser(false);
            }
        };
        if (selectedProject && loggedInUser) fetchUserAssignment();
    }, [selectedProject, loggedInUser, selectedTargetUserId]);

    // Fetch users in selected project (for eigenaar/financieel_medewerker)
    useEffect(() => {
        const fetchProjectUsers = async () => {
            try {
                if (!selectedProject || !loggedInUser) return;

                // Only fetch project users if logged-in user is eigenaar or financieel_medewerker
                if (loggedInUser.role === "eigenaar" || loggedInUser.role === "financieel medewerker") {
                    console.log("Fetching users for project:", selectedProject.id);
                    const usersRes = await api.get(`/projects/${selectedProject.id}/users`);
                    console.log("Project users response:", usersRes.data);
                    const users = usersRes.data || [];
                    setProjectUsers(users);

                    // Auto-select the logged-in user if they are part of the project
                    if (users.length > 0 && !selectedTargetUserId) {
                        const loggedInUserInProject = users.find(u => u.user_id === loggedInUser.id);
                        if (loggedInUserInProject) {
                            setSelectedTargetUserId(loggedInUserInProject.user_id);
                            setTargetUser(loggedInUserInProject);
                            setRateType(loggedInUserInProject.rate_type);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching project users:", error);
                setProjectUsers([]);
            }
        };
        if (selectedProject && loggedInUser) fetchProjectUsers();
    }, [selectedProject, loggedInUser]);

    // Load initial timesheet data
    useEffect(() => {
        if (initialTimesheet && projects.length > 0) {
            setCurrentDate(initialTimesheet.week_start_date ? new Date(initialTimesheet.week_start_date) : new Date());
            setHours(initialTimesheet.hours_worked
                ? Array.isArray(initialTimesheet.hours_worked)
                    ? initialTimesheet.hours_worked
                    : objectToDayArray(initialTimesheet.hours_worked)
                : Array(7).fill(0)
            );
            setMeters(initialTimesheet.meters_worked
                ? Array.isArray(initialTimesheet.meters_worked)
                    ? initialTimesheet.meters_worked
                    : objectToDayArray(initialTimesheet.meters_worked)
                : Array(7).fill(0)
            );
            setExtraWork(initialTimesheet.extra_work || []);
            setMaterials(initialTimesheet.materials || []);
            const found = projects.find(p => p.id === initialTimesheet.project_id);
            if (found) setSelectedProject(found);
        }
    }, [initialTimesheet, projects]);

    const getWeekRange = useCallback(() => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return { startOfWeek, endOfWeek };
    }, [currentDate]);

    const handlePreviousWeek = useCallback(() => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() - 7);
        setCurrentDate(newDate);
    }, [currentDate]);

    const handleNextWeek = useCallback(() => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + 7);
        setCurrentDate(newDate);
    }, [currentDate]);

    const handleHoursChange = useCallback((index, value) => {
        const updatedHours = [...hours];
        // Allow empty string, numbers, and partial decimals (e.g., "3.", "3.5")
        if (value === '' || value === null) {
            updatedHours[index] = 0;
        } else {
            // Replace comma with period for European decimal input
            const normalizedValue = value.toString().replace(',', '.');
            const numValue = parseFloat(normalizedValue);
            // Only update if it's a valid number, otherwise keep as 0
            updatedHours[index] = isNaN(numValue) ? 0 : numValue;
        }
        setHours(updatedHours);
    }, [hours]);

    const handleMetersChange = useCallback((index, value) => {
        const updatedMeters = [...meters];
        // Allow empty string, numbers, and partial decimals (e.g., "3.", "3.5")
        if (value === '' || value === null) {
            updatedMeters[index] = 0;
        } else {
            // Replace comma with period for European decimal input
            const normalizedValue = value.toString().replace(',', '.');
            const numValue = parseFloat(normalizedValue);
            // Only update if it's a valid number, otherwise keep as 0
            updatedMeters[index] = isNaN(numValue) ? 0 : numValue;
        }
        setMeters(updatedMeters);
    }, [meters]);

    const handleAddExtraWork = useCallback(() => {
        setExtraWork([...extraWork, { omschrijving: "", opmerkingen: "", foto: null, preview: null }]);
    }, [extraWork]);

    const handleExtraWorkChange = useCallback((index, field, value) => {
        const updated = [...extraWork];

        if (field === "foto") {
            const file = value.target.files[0];

            // Validate file
            try {
                validateFile(file, { required: false });

                // Clean up old preview URL
                if (updated[index].preview) {
                    revokePreviewUrls(updated[index].preview);
                }

                // Create new preview
                const preview = createPreviewUrl(file);
                previewUrlsRef.current.push(preview);

                updated[index].foto = file;
                updated[index].preview = preview;
            } catch (error) {
                if (error instanceof FileValidationError) {
                    showFlash?.(error.message, "error");
                }
                return;
            }
        } else {
            updated[index][field] = value;
        }

        setExtraWork(updated);
    }, [extraWork, showFlash]);

    const handleRemoveExtraWork = useCallback((index) => {
        const updated = [...extraWork];

        // Clean up preview URL
        if (updated[index].preview) {
            revokePreviewUrls(updated[index].preview);
            previewUrlsRef.current = previewUrlsRef.current.filter(url => url !== updated[index].preview);
        }

        updated.splice(index, 1);
        setExtraWork(updated);
    }, [extraWork]);

    const getWeekNumber = useCallback((date) => {
        const copiedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        copiedDate.setUTCDate(copiedDate.getUTCDate() + 4 - (copiedDate.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(copiedDate.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil((((copiedDate - yearStart) / 86400000) + 1) / 7);
        return weekNo;
    }, []);

    const handleAddMaterial = useCallback(() => {
        setMaterials([...materials, {
            name: "",
            description: "",
            quantity: 0,
            unit: "",
            cost: 0,
            bonnetje: null,
            preview: null
        }]);
    }, [materials]);

    const handleMaterialChange = useCallback((index, field, value) => {
        const updated = [...materials];
        updated[index][field] = value;
        setMaterials(updated);
    }, [materials]);

    const handleMaterialFileChange = useCallback((index, file) => {
        const updated = [...materials];

        // Validate file
        try {
            validateFile(file, { required: false });

            // Clean up old preview URL
            if (updated[index].preview) {
                revokePreviewUrls(updated[index].preview);
            }

            // Create new preview
            const preview = createPreviewUrl(file);
            previewUrlsRef.current.push(preview);

            updated[index].bonnetje = file;
            updated[index].preview = preview;
            setMaterials(updated);
        } catch (error) {
            if (error instanceof FileValidationError) {
                showFlash?.(error.message, "error");
            }
        }
    }, [materials, showFlash]);

    const handleRemoveMaterial = useCallback((index) => {
        const updated = [...materials];

        // Clean up preview URL
        if (updated[index].preview) {
            revokePreviewUrls(updated[index].preview);
            previewUrlsRef.current = previewUrlsRef.current.filter(url => url !== updated[index].preview);
        }

        updated.splice(index, 1);
        setMaterials(updated);
    }, [materials]);

    const handleTargetUserChange = useCallback((userId) => {
        const targetUserId = parseInt(userId);
        setSelectedTargetUserId(targetUserId);

        // Find the user details from projectUsers
        const selectedUser = projectUsers.find(u => u.user_id === targetUserId);
        if (selectedUser) {
            setTargetUser(selectedUser);
            setRateType(selectedUser.rate_type);
        }
    }, [projectUsers]);

    const calculateTotals = useCallback(() => {
        const hoursTotal = hours.reduce((acc, cur) => acc + cur, 0);
        const metersTotal = meters.reduce((acc, cur) => acc + cur, 0);
        const materialsTotal = materials.reduce((acc, cur) =>
            acc + ((parseFloat(cur.quantity) || 0) * (parseFloat(cur.cost) || 0)), 0
        );
        setTotalHours(rateType === "meter" ? metersTotal : hoursTotal);
        setTotalCosts(materialsTotal);
    }, [hours, meters, materials, rateType]);

    const handleGeneratePDF = useCallback(async () => {
        const weekRange = getWeekRange();
        const userForPDF = { ...user, name: user.user_name };
        await generatePDF({
            user: userForPDF,
            selectedProject,
            hours,
            meters,
            extraWork,
            materials,
            totalHours,
            totalCosts,
            weekRange,
            rateType
        });
    }, [user, selectedProject, hours, meters, extraWork, materials, totalHours, totalCosts, getWeekRange, rateType]);

    // Prepare payload data
    const preparePayload = useCallback(() => {
        const { startOfWeek } = getWeekRange();

        const hoursWorked = {
            monday: hours[0],
            tuesday: hours[1],
            wednesday: hours[2],
            thursday: hours[3],
            friday: hours[4],
            saturday: hours[5],
            sunday: hours[6]
        };

        const metersWorked = {
            monday: meters[0],
            tuesday: meters[1],
            wednesday: meters[2],
            thursday: meters[3],
            friday: meters[4],
            saturday: meters[5],
            sunday: meters[6]
        };

        // Prepare materials (remove file objects, keep only data)
        const sanitizedMaterials = materials
            .filter(m => m.name && m.description && m.quantity && m.unit && m.cost)
            .map(({ bonnetje, preview, ...rest }) => ({
                ...rest,
                quantity: Number(rest.quantity) || 0,
                cost: Number(rest.cost) || 0
            }));

        // Prepare extra work (remove file objects, keep only data)
        const sanitizedExtraWork = extraWork
            .filter(w => w.omschrijving)
            .map(({ foto, preview, ...rest }) => rest);

        const payload = {
            project_id: selectedProject.id,
            week_start_date: startOfWeek.toISOString(),
            rate_type: rateType,
            hours_worked: hoursWorked,
            meters_worked: metersWorked,
            extra_work: sanitizedExtraWork,
            materials: sanitizedMaterials,
            notes: initialTimesheet?.notes || ""
        };

        // Determine which user to use for project_role_id
        // Priority: targetUser (if selected) > user (logged-in user assignment)
        const userForRole = targetUser || user;

        // Add project_role_id - REQUIRED by backend
        if (userForRole?.project_role_id) {
            payload.project_role_id = userForRole.project_role_id;
        } else {
            // Fallback: try to find the project_role_id from selectedTargetUserId
            if (selectedTargetUserId && projectUsers.length > 0) {
                const foundUser = projectUsers.find(u => u.user_id === selectedTargetUserId);
                if (foundUser?.project_role_id) {
                    payload.project_role_id = foundUser.project_role_id;
                }
            }
        }

        // Add target_user_id if a different user is selected
        if (selectedTargetUserId && selectedTargetUserId !== loggedInUser?.id) {
            payload.target_user_id = selectedTargetUserId;
        }

        return payload;
    }, [getWeekRange, hours, meters, materials, extraWork, selectedProject, user, targetUser, rateType, initialTimesheet, selectedTargetUserId, loggedInUser, projectUsers]);

    // Check if any files need to be uploaded
    const hasFiles = useCallback(() => {
        return extraWork.some(w => w.foto instanceof File) ||
               materials.some(m => m.bonnetje instanceof File);
    }, [extraWork, materials]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const materialValidation = validateMaterials(materials);
        const extraWorkValidation = validateExtraWork(extraWork);

        if (!materialValidation.isValid) {
            showFlash?.(
                `Er zijn ${materialValidation.invalidCount} onvolledige materiaalregel(s). Verwijder of vul deze eerst in.`,
                "error"
            );
            return;
        }

        if (!extraWorkValidation.isValid) {
            showFlash?.(
                `Er zijn ${extraWorkValidation.invalidCount} onvolledige meerwerkregel(s). Verwijder of vul deze eerst in.`,
                "error"
            );
            return;
        }

        setSubmitting(true);
        setUploadProgress(0);

        try {
            const payload = preparePayload();
            // handleSubmit is for SUBMITTING for approval, not saving as draft
            // Status will be 'pending' by default (set by backend or not set at all)

            let response;

            if (hasFiles()) {
                // Collect files for FormData
                const extraWorkFiles = extraWork
                    .map((w, idx) => ({ file: w.foto, index: idx }))
                    .filter(item => item.file instanceof File);

                const materialFiles = materials
                    .map((m, idx) => ({ file: m.bonnetje, index: idx }))
                    .filter(item => item.file instanceof File);

                // Build FormData
                const formData = buildFormData(payload, [
                    {
                        dataKey: 'extra_work',
                        files: extraWorkFiles.map(item => item.file),
                        formKey: 'extra_work_photos'
                    },
                    {
                        dataKey: 'materials',
                        files: materialFiles.map(item => item.file),
                        formKey: 'material_receipts'
                    }
                ]);

                // Upload with progress
                const endpoint = initialTimesheet?.id
                    ? `/timesheets/${initialTimesheet.id}`
                    : '/timesheets';

                const method = initialTimesheet?.id ? 'put' : 'post';

                response = await uploadWithProgress(
                    endpoint,
                    formData,
                    setUploadProgress,
                    api
                );
            } else {
                // JSON upload (no files)
                if (initialTimesheet?.id) {
                    response = await api.put(`/timesheets/${initialTimesheet.id}`, payload);
                } else {
                    response = await api.post('/timesheets', payload);
                }
            }

            const id = response.data.id || initialTimesheet?.id;
            showFlash?.(t('success.timesheetSubmitted'), "success");

            // Clean up preview URLs before navigation
            revokePreviewUrls(previewUrlsRef.current);
            previewUrlsRef.current = [];

            navigate(`/timesheets/${id}`);
        } catch (error) {
            console.error("Error submitting timesheet:", error);
            const errorMessage = error.response?.data?.message || "Fout bij opslaan van weekstaat.";
            showFlash?.(errorMessage, "error");
        } finally {
            setSubmitting(false);
            setUploadProgress(0);
        }
    };

    const handleSaveAsDraft = async () => {
        if (!selectedProject) {
            showFlash?.("Selecteer een project.", "error");
            return;
        }

        if (!user) {
            showFlash?.("Gebruikersgegevens niet gevonden.", "error");
            return;
        }

        setSubmitting(true);
        setUploadProgress(0);

        try {
            const payload = preparePayload();
            payload.status = 'draft'; // Set status to draft

            // Validate required fields
            if (!payload.project_role_id) {
                showFlash?.("Geen projectrol gevonden. Zorg dat je toegewezen bent aan dit project.", "error");
                setSubmitting(false);
                setUploadProgress(0);
                return;
            }

            console.log('Draft payload:', payload);

            let response;

            if (hasFiles()) {
                // Collect files for FormData
                const extraWorkFiles = extraWork
                    .map((w, idx) => ({ file: w.foto, index: idx }))
                    .filter(item => item.file instanceof File);

                const materialFiles = materials
                    .map((m, idx) => ({ file: m.bonnetje, index: idx }))
                    .filter(item => item.file instanceof File);

                // Build FormData
                const formData = buildFormData(payload, [
                    {
                        dataKey: 'extra_work',
                        files: extraWorkFiles.map(item => item.file),
                        formKey: 'extra_work_photos'
                    },
                    {
                        dataKey: 'materials',
                        files: materialFiles.map(item => item.file),
                        formKey: 'material_receipts'
                    }
                ]);

                const endpoint = initialTimesheet?.id
                    ? `/timesheets/${initialTimesheet.id}`
                    : '/timesheets';

                response = await uploadWithProgress(
                    endpoint,
                    formData,
                    setUploadProgress,
                    api
                );
            } else {
                // JSON upload (no files)
                if (initialTimesheet?.id) {
                    response = await api.put(`/timesheets/${initialTimesheet.id}`, payload);
                } else {
                    response = await api.post('/timesheets', payload);
                }
            }

            const id = response.data.id || initialTimesheet?.id;
            showFlash?.(t('success.draftSaved'), "success");

            // Clean up preview URLs before navigation
            revokePreviewUrls(previewUrlsRef.current);
            previewUrlsRef.current = [];

            navigate(`/timesheets/${id}`);
        } catch (error) {
            console.error("Error saving draft:", error);
            const errorMessage = error.response?.data?.message || "Fout bij opslaan van concept.";
            showFlash?.(errorMessage, "error");
        } finally {
            setSubmitting(false);
            setUploadProgress(0);
        }
    };

    useEffect(() => {
        calculateTotals();
    }, [calculateTotals]);

    return {
        currentDate,
        setCurrentDate,
        hours,
        setHours,
        meters,
        setMeters,
        extraWork,
        setExtraWork,
        materials,
        setMaterials,
        projects,
        setProjects,
        selectedProject,
        setSelectedProject,
        user,
        loadingUser,
        loggedInUser,
        targetUser,
        projectUsers,
        selectedTargetUserId,
        handleTargetUserChange,
        totalHours,
        totalCosts,
        rateType,
        setRateType,
        submitting,
        uploadProgress,
        showConfirmModal,
        setShowConfirmModal,
        units,
        daysOfWeek,
        getWeekRange,
        handlePreviousWeek,
        handleNextWeek,
        handleHoursChange,
        handleMetersChange,
        handleAddExtraWork,
        handleExtraWorkChange,
        handleRemoveExtraWork,
        getWeekNumber,
        handleAddMaterial,
        handleMaterialChange,
        handleMaterialFileChange,
        handleRemoveMaterial,
        handleGeneratePDF,
        handleSubmit,
        handleSaveAsDraft
    };
}
